// Console diagnostics for the RealtimeKit host panel.
//
// Every line is prefixed with "[RTK]" so the browser console can be filtered
// with that string. Run the app with `npm run dev` — vite.config.js sets
// terser `drop_console: true`, so ALL of these lines are stripped from
// `npm run build` output and will never appear in a production build.

export const RTK_TAG = "[RTK]";

export function rtkLog(...args) {
  console.log(RTK_TAG, ...args);
}

export function rtkWarn(...args) {
  console.warn(RTK_TAG, ...args);
}

export function rtkError(...args) {
  console.error(RTK_TAG, ...args);
}

/** Shape of a secret, never the secret itself. */
export function describeToken(token) {
  if (!token) return { present: false };
  const value = String(token);
  return {
    present: true,
    length: value.length,
    head: `${value.slice(0, 6)}…${value.slice(-4)}`,
  };
}

function describeEl(selector) {
  const el = document.querySelector(selector);
  if (!el) return { found: false };
  const cs = getComputedStyle(el);
  const rect = el.getBoundingClientRect();
  return {
    found: true,
    // Was the web component registered at all?
    defined: Boolean(customElements.get(selector)),
    // Stencil adds `.hydrated` only after the component's first successful
    // render. Without it Stencil's bootstrap styles keep it visibility:hidden.
    hydrated: el.classList.contains("hydrated"),
    visibility: cs.visibility,
    display: cs.display,
    box: `${Math.round(rect.width)}x${Math.round(rect.height)}`,
    hasShadow: Boolean(el.shadowRoot),
    // A defined Stencil component renders into its shadow root; an undefined
    // tag stays a plain empty element (this is what a black player looks like).
    shadowHtmlLen: el.shadowRoot ? el.shadowRoot.innerHTML.length : null,
    lightChildren: el.children.length,
  };
}

/**
 * Answers "is the Stencil UI actually rendering, or is the panel just black?".
 * Returns a plain object so it can be logged, copied and pasted back.
 */
export function probeRtkDom() {
  const customElementsApi = typeof customElements !== "undefined" ? customElements : undefined;
  const tags = ["rtk-ui-provider", "rtk-grid", "rtk-mic-toggle", "rtk-leave-button"];
  const definedTags = customElementsApi
    ? tags.filter((t) => Boolean(customElementsApi.get(t)))
    : [];

  return {
    customElementsSupported: Boolean(customElementsApi),
    // bootstrapLazy() injects <style data-styles> — its presence means the
    // SDK registered its components.
    bootstrapStylesInjected: Boolean(document.querySelector("style[data-styles]")),
    definedTags,
    provider: describeEl("rtk-ui-provider"),
    grid: describeEl("rtk-grid"),
    micToggle: describeEl("rtk-mic-toggle"),
    leaveButton: describeEl("rtk-leave-button"),
  };
}

/**
 * True when nothing inside the player can be rendering — the state that shows
 * up as a completely black box.
 */
export function rtkUiIsBroken(probe = probeRtkDom()) {
  if (!probe.customElementsSupported) return true;
  // bootstrapLazy() never ran → every rtk-* tag stays an empty unknown element
  // (no grid, no control-bar icons) which paints as a completely black box.
  if (!probe.bootstrapStylesInjected) return true;
  if (!probe.provider.found) return false; // not joined yet — nothing to judge
  if (!probe.provider.defined) return true;
  // Registered but never hydrated → Stencil's own styles keep it
  // visibility:hidden, so nothing inside it (including React's own markup) is
  // painted. Also a black box.
  if (!probe.provider.hydrated) return true;
  return probe.provider.visibility !== "visible";
}
